import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings2, Network, Database, TriangleAlert, Wrench, Activity } from 'lucide-react';
import Interfaces from './Interfaces';
import BackupRestore from './BackupRestore';
import DangerZone from './DangerZone';
import GlobalSettings from './GlobalSettings';
import Maintenance from './Maintenance';
import Health from './Health';

const SystemSection = ({
  isDarkMode,
  edgeServerEndpoint,
  showToast,
  globalSettings,
  handleGlobalChange,
  timezone,
  timeFormat,
  setTimeFormat,
  reverseSort,
  setReverseSort,
  user,
  handleBackupNow,
  keywords,
  newKeyword,
  setNewKeyword,
  handleAddKeyword,
  handleRemoveKeyword
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('hotspot-configuration');

  // Handle URL parameter for system settings tab
  useEffect(() => {
    const systemTab = searchParams.get('systemTab');
    if (systemTab && ['interfaces', 'hotspot-configuration', 'backup-restore', 'maintenance', 'danger-zone', 'health'].includes(systemTab)) {
      setActiveTab(systemTab);
    } else {
      // Default to hotspot-configuration if no tab specified
      setActiveTab('hotspot-configuration');
    }
  }, [searchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: 'system', systemTab: tabId });
  };

  // Render GlobalSettings section with the specified activeSection
  const renderGlobalSettings = () => (
    <GlobalSettings
      globalSettings={globalSettings}
      handleGlobalChange={handleGlobalChange}
      isDarkMode={isDarkMode}
      timezone={timezone}
      timeFormat={timeFormat}
      setTimeFormat={setTimeFormat}
      reverseSort={reverseSort}
      setReverseSort={setReverseSort}
      user={user}
      edgeServerEndpoint={edgeServerEndpoint}
      activeSection={activeTab}
    />
  );

  const containerClasses = isDarkMode
    ? 'bg-gray-900/60 border-gray-800'
    : 'bg-white border-gray-200';

  return (
    <div className={`rounded-2xl shadow-lg p-4 md:p-6 transition-colors duration-300 border ${containerClasses}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-2xl shadow-md ${isDarkMode ? 'bg-gray-700' : 'bg-gray-600'}`}>
            <Settings2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">System</h2>
            <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Manage interfaces, hotspot configuration, backups, and maintenance tools.
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} overflow-x-auto`}>
        <nav className="flex space-x-4 md:space-x-8 min-w-max" aria-label="Tabs">
          <button
            onClick={() => handleTabChange('interfaces')}
            className={`
              flex items-center gap-1 md:gap-2 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex-shrink-0
              ${activeTab === 'interfaces'
                ? isDarkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-600 text-blue-600'
                : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Network className="w-4 h-4 md:w-5 md:h-5" />
            Interfaces
          </button>
          <button
            onClick={() => handleTabChange('hotspot-configuration')}
            className={`
              flex items-center gap-1 md:gap-2 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex-shrink-0
              ${activeTab === 'hotspot-configuration'
                ? isDarkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-600 text-blue-600'
                : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Network className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Hotspot Configuration</span>
            <span className="sm:hidden">Hotspot</span>
          </button>
          <button
            onClick={() => handleTabChange('backup-restore')}
            className={`
              flex items-center gap-1 md:gap-2 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex-shrink-0
              ${activeTab === 'backup-restore'
                ? isDarkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-600 text-blue-600'
                : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Database className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Backup & Restore</span>
            <span className="sm:hidden">Backup</span>
          </button>
          <button
            onClick={() => handleTabChange('maintenance')}
            className={`
              flex items-center gap-1 md:gap-2 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex-shrink-0
              ${activeTab === 'maintenance'
                ? isDarkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-600 text-blue-600'
                : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Wrench className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Maintenance</span>
            <span className="sm:hidden">Maint</span>
          </button>
          <button
            onClick={() => handleTabChange('health')}
            className={`
              flex items-center gap-1 md:gap-2 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex-shrink-0
              ${activeTab === 'health'
                ? isDarkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-600 text-blue-600'
                : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Activity className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Health</span>
            <span className="sm:hidden">Health</span>
          </button>
          <button
            onClick={() => handleTabChange('danger-zone')}
            className={`
              flex items-center gap-1 md:gap-2 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex-shrink-0
              ${activeTab === 'danger-zone'
                ? isDarkMode
                  ? 'border-red-500 text-red-400'
                  : 'border-red-600 text-red-600'
                : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <TriangleAlert className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Danger Zone</span>
            <span className="sm:hidden">Danger</span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {activeTab === 'interfaces' && (
          <Interfaces 
            isDarkMode={isDarkMode} 
          />
        )}
        {activeTab === 'hotspot-configuration' && (
          renderGlobalSettings()
        )}
        {activeTab === 'backup-restore' && (
          <BackupRestore 
            isDarkMode={isDarkMode} 
            edgeServerEndpoint={edgeServerEndpoint}
            showToast={showToast}
            globalSettings={globalSettings}
            handleGlobalChange={handleGlobalChange}
            handleBackupNow={handleBackupNow}
          />
        )}
        {activeTab === 'maintenance' && (
          <Maintenance 
            edgeServerEndpoint={edgeServerEndpoint} 
            isDarkMode={isDarkMode} 
            showToast={showToast}
          />
        )}
        {activeTab === 'health' && (
          <Health 
            edgeServerEndpoint={edgeServerEndpoint} 
            isDarkMode={isDarkMode} 
          />
        )}
        {activeTab === 'danger-zone' && (
          <DangerZone 
            edgeServerEndpoint={edgeServerEndpoint} 
            isDarkMode={isDarkMode} 
            showToast={showToast}
          />
        )}
      </div>
    </div>
  );
};

export default SystemSection;

